import {
  FlexibleConnectedPositionStrategyOrigin,
  Overlay,
  OverlayPositionBuilder,
  OverlayRef,
} from '@angular/cdk/overlay';
import { Injectable } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { ComponentPortal } from '@angular/cdk/portal';
import { SearchOptionsOverlayComponent } from '../components/search/search-options/search-options-overlay/search-options-overlay.component';
import { OverlayPositions } from '../utility/constants';

@Injectable({
  providedIn: 'root',
})
export class SearchOverlayService {
  private searchOverlayRef?: OverlayRef;
  private hyperlinkSub?: Subscription;
  private outsideClickSub?: Subscription;

  constructor(
    private readonly overlayPositionBuilder: OverlayPositionBuilder,
    private readonly overlay: Overlay
  ) {}

  public get isOverlayOpen(): boolean {
    return !!this.searchOverlayRef?.hasAttached();
  }

  public openSearchOverlay(
    searchOverlay: FlexibleConnectedPositionStrategyOrigin,
    isFromModule: string,
    focusFirstItem = false
  ) {
    this.closeOverlay();
    this.getOverlayConfig(searchOverlay);
    const tooltipPortal = new ComponentPortal(SearchOptionsOverlayComponent);
    const tooltipRef = this.searchOverlayRef!.attach(tooltipPortal);
    tooltipRef.instance.isFrom = isFromModule;
    tooltipRef.instance.focusFirstItem = focusFirstItem;
    this.hyperlinkSub = tooltipRef.instance.hyperlinkClicked
      .pipe(take(1))
      .subscribe(() => {
        this.closeOverlay();
      });
    this.listenForOutsideClick();
  }

  public closeOverlay() {
    this.hyperlinkSub?.unsubscribe();
    this.hyperlinkSub = undefined;
    this.outsideClickSub?.unsubscribe();
    this.outsideClickSub = undefined;
    if (this.searchOverlayRef) {
      this.searchOverlayRef.dispose();
      this.searchOverlayRef = undefined;
    }
  }

  // Close on a click outside the overlay. Clicks inside any CDK overlay (this
  // panel, dialogs opened from it, tooltips) are ignored. The timestamp check
  // skips the click that opened the overlay, which is still bubbling up to the
  // document when this listener is added.
  private listenForOutsideClick() {
    const openedAt = performance.now();
    this.outsideClickSub = fromEvent<MouseEvent>(document, 'click')
      .pipe(
        filter(event => event.timeStamp > openedAt),
        filter(
          event =>
            !(event.target as HTMLElement | null)?.closest(
              '.cdk-overlay-container'
            )
        )
      )
      .subscribe(() => this.closeOverlay());
  }

  private getOverlayConfig(
    searchOverlay: FlexibleConnectedPositionStrategyOrigin
  ) {
    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(searchOverlay)
      .withPositions([
        {
          originX: OverlayPositions.end,
          originY: OverlayPositions.top,
          overlayX: OverlayPositions.end,
          overlayY: OverlayPositions.top,
          offsetX: 0,
          offsetY: 10,
        },
      ]);
    this.searchOverlayRef = this.overlay.create({
      positionStrategy,
      disposeOnNavigation: true,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });
  }
}
